import React from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import firebase from 'firebase';
import { Visibility, VisibilityOff } from 'styled-icons/material';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Notification } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { useHistory } from 'react-router-dom';
import { palette } from '../../system/styles/styles';
import { ShowNotification } from '../../components/Notifications';
import { adminLogin } from '../../redux/actions/_auth_actions';
import { startShowUsers } from '../../redux/actions/_users_actions';
import { showCompaniesAdmin } from '../../redux/actions/_companies_actions';

const loginAdminDefaultImage =
  'https://firebasestorage.googleapis.com/v0/b/universia-prod.appspot.com/o/defaultImages%2FAdminLogin.jpg?alt=media&token=1c081888-7de6-4b3a-817d-9ea5f8650b25';

function Login(props) {
  const { className, history } = props;
  const dispatch = useDispatch();
  const [state, setState] = React.useState({
    email: '',
    password: '',
    message: '',
    showPassword: false,
    showMessage: false,
    error: null,
    open: false,
    emailToRecover: '',
  });

  const platform = useSelector((state) => state.platform);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { email, password } = state;
      const user = await firebase.auth().signInWithEmailAndPassword(email, password);
      if (user.user.emailVerified) {
        const idTokenResult = await firebase.auth().currentUser.getIdTokenResult();
        const { typeuser, segment = '' } = idTokenResult.claims;
        const isSamePlatform = segment.toUpperCase() === platform.platformName.toUpperCase();
        const lapiezaAccess = segment === 'lapiezaAccess';
        const isAdminUser = typeuser === 'adminUser';
        if (isAdminUser) {
          if (isSamePlatform || lapiezaAccess) {
            dispatch(adminLogin(user.user.uid, idTokenResult.claims.typeuser));
            dispatch(startShowUsers());
            dispatch(showCompaniesAdmin());
            history.push('/management/dashboard');
          } else {
            ShowNotification(
              'error',
              'No existe ningun usuario ',
              'No se encontro ningún usuario asociado a este correo electrónico'
            );
            await firebase.auth().signOut();
          }
        } else {
          ShowNotification(
            'error',
            'Este apartado esta restringido por favor evita sanciones en la plataforma.',
            'Usuario Incorrecto'
          );
          await firebase.auth().signOut();
        }
      } else {
        await firebase.auth().signOut();
        setState((prevState) => ({
          ...prevState,
          error: 'Verifica tu correo',
          message:
            'Es necesario que verifiques tu cuenta de correo electrónico, ve a tu bandeja de entrada y da click en el enlace que te hemos enviado.',
          showMessage: true,
        }));
        ShowNotification('error', state.error, 'Verifica tu cuenta...');
      }
    } catch (error) {
      const errorCode = error.code;
      if (errorCode === 'auth/invalid-email' || errorCode === 'auth/wrong-password') {
        setState((prevState) => ({
          ...prevState,
          error: 'Correo o contraseña invalida',
          message: 'El correo o contraseña que ingresaste es incorrecto, por favor verifica e intenta de nuevo',
          showMessage: true,
        }));
      } else if (errorCode === 'auth/user-not-found') {
        setState((prevState) => ({
          ...prevState,
          error: 'No se encontro usuario',
          message:
            'No encontramos ningún usuario registrado con este correo. Si aún no tienes cuenta, regístrate.',
          showMessage: true,
        }));
      } else {
        setState((prevState) => ({ ...prevState, error }));
      }
      ShowNotification('error', 'Hubo un error intentalo más tarde', 'Algo salio mal...');
    }
  };

  const togglePassword = () => {
    setState((prevState) => ({ ...prevState, showPassword: !state.showPassword }));
  };

  const passwordRecovery = () => {
    setState((prevState) => ({
      ...prevState,
      open: !prevState.open,
    }));
  };

  const sendRecoverPassword = async (e) => {
    e.preventDefault();
    const re =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (re.test(state.emailToRecover)) {
      try {
        await firebase.auth().sendPasswordResetEmail(state.emailToRecover);
        setState((prevState) => ({
          ...prevState,
          linkSent: true,
          linkSentError: false,
          message:
            'Hemos enciviado un link de recuperación al correo que proporcionaste, restablece tu contraseña e inicia sesión',
        }));
        ShowNotification('success', 'Hemos enviado el link de recuperacion a tu correo', 'Todo listo');
      } catch (e) {
        if (e.code === 'auth/user-not-found') {
          await setState((prevState) => ({
            ...prevState,
            linkSentError: true,
            linkSent: false,
            message:
              'No existe ninguna cuenta asociada a este correo electrónico o tal vez fue eliminada de la plataforma, contacta al administrador de la institución para más información.',
          }));
        }
      }
    } else {
      ShowNotification('warning', 'Ingresa un correo valido', 'Correo electrónico invalido');
    }
  };

  return (
    <section className={className}>
      <div className="login">
        <img
          src={platform.customLoginAdminImage === '' ? loginAdminDefaultImage : platform.customLoginAdminImage}
          className="leftImage"
          alt=""
        />
        <div className="FormBox">
          <form className="FormContainer" onSubmit={handleSubmit} autoComplete="off">
            <h1>Iniciar sesión.</h1>
            <input required="" type="email" name="email" placeholder="Usuario" onChange={handleInputChange} />
            <div className="TogglePasswordContainer">
              <input
                id="password"
                type={state.showPassword ? 'text' : 'password'}
                value={state.password}
                name="password"
                onChange={handleInputChange}
                placeholder="Contraseña"
              />
              {state.showPassword ? (
                <Visibility onClick={togglePassword} size={16} className="TogglePassword" />
              ) : (
                <VisibilityOff onClick={togglePassword} size={16} className="TogglePassword" />
              )}
            </div>
            {state.showMessage ? <p className="error-message">{state.message}</p> : null}
            <button type="submit">Entrar</button>
            <p onClick={passwordRecovery}>¿Olvidaste tu contraseña?</p>
          </form>
        </div>
        {state.open ? (
          <Dialog
            title="Recuperar cuenta"
            onClick={passwordRecovery}
            onClose={() => setState((prevState) => ({ ...prevState, open: false }))}
            className={className}>
            <div className="FormBoxDialog">
              <p className="recovery-title">Para recuperar el acceso a tu cuenta ingresa tu correo electrónico</p>
              <p className="recovery-subtitle">Recibirás un link para establecer una nueva contraseña</p>
              <form onSubmit={sendRecoverPassword}>
                <input
                  name="emailToRecover"
                  value={state.emailToRecover}
                  onChange={handleInputChange}
                  label="Correo electrónico"
                  variant="outlined"
                  placeholder="Usuario"
                  required
                  type="email"
                />
                <div className="modalButtonContainer">
                  <button
                    type="button"
                    className="cancel"
                    onClick={() => setState((prevState) => ({ ...prevState, open: false }))}>
                    Cancelar
                  </button>
                  <button type="submit" className="submit-btn">
                    Recuperar contraseña
                  </button>
                </div>
              </form>
              {state.linkSent ? (
                <Fade>
                  <Notification
                    type={{
                      style: 'success',
                      icon: true,
                    }}>
                    <span>Hemos enviado un link de recuperación de contraseña al correo electrónico que indicaste</span>
                  </Notification>
                </Fade>
              ) : null}
              {state.linkSentError ? (
                <Fade>
                  <Notification
                    type={{
                      style: 'warning',
                      icon: true,
                    }}>
                    <span>{state.message}</span>
                  </Notification>
                </Fade>
              ) : null}
            </div>
          </Dialog>
        ) : null}
      </div>
    </section>
  );
}

export default styled(Login)`
  .login {
    width: 100%;
    padding: 0px;
    display: grid;
    grid-template-columns: 40% 60%;
    max-height: 100vh;
    min-height: 100vh;
    background-color: ${palette.white};
    font-family: 'Sofia Pro', sans-serif;
  }
  .leftImage {
    width: 100%;
    height: 100%;
    object-position: center;
    object-fit: cover;
  }

  .FormBox {
    width: 100%;
    position: relative;
    background-color: ${palette.white};
    padding: 50px 32px 100px 32px;
    @media screen and (max-width: 1441px) {
      padding: 16px 16px 100px 16px;
    }
    @media screen and (max-width: 769px) {
      min-height: calc(100vh - 80px);
    }
    @media screen and (max-width: 321px) {
      padding: 50px 16px 100px 16px;
    }
  }

  .FormBoxDialog {
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: ${palette.white};
    padding: 30px;
    form {
      display: flex;
      width: 75%;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
      input {
        width: 100%;
        margin: 15px 0px 35px 0px !important;
        border: none;
        border-bottom: 1px solid #bfbebe !important;
        padding: 12px 8px !important;
        fontsize: 2rem !important;
        color: ${palette.black};
      }
    }
    @media screen and (max-width: 1441px) {
      padding: 16px 16px 100px 16px;
    }
    @media screen and (max-width: 769px) {
      min-height: calc(100vh - 80px);
    }
    @media screen and (max-width: 321px) {
      padding: 50px 16px 100px 16px;
    }
  }

  .modalButtonContainer {
    width: 100%;
    padding: 10px;
    display: flex;
    align-items: flex-end;
    margin: 20px 0px;
    justify-content: space-around;
    button {
      width: auto;
      padding: 10px 20px;
      font-size: 1.2rem;
      margin-top: 20px;
      text-align: center;
      cursor: pointer;
      @media screen and (max-width: 769px) {
        margin-top: 20px;
      }
    }
  }

  .recovery-title {
    width: 90%;
    font-size: 1.9rem;
    text-align: center;
    margin-top: 25px;
    margin-bottom: 25px;
    font-family: 'Cooper Md BT', sans-serif;
  }

  .recovery-subtitle {
    width: 90%;
    font-size: 1.2rem;
    text-align: center;
    margin-top: 25px;
    margin-bottom: 25px;
  }

  .FormContainer {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    h1 {
      font-size: 32px;
      font-weight: 500;
      font-family: 'Cooper Md BT', sans-serif;
    }
    input {
      width: 100%;
      margin: 15px 0px 35px 0px !important;
      border: none;
      border-bottom: 1px solid #bfbebe !important;
      padding: 12px 8px !important;
      fontsize: 2rem !important;
      color: ${palette.black};
    }
    button {
      color: #fff;
      background-color: #0f3f44;
      display: block;
      margin-top: 16px;
      width: 100%;
      padding: 16px 32px;
      border-radius: 32px;
      margin-top: 32px;
      border: none;
      &:hover {
        cursor: pointer;
      }
    }
    p {
      width: auto;
      color: #bfbebe;
      padding: 10px 20px;
      font-size: 1.2rem;
      margin-top: 20px;
      text-align: center;
      cursor: pointer;
      font-family: 'Cooper Md BT', sans-serif;
      @media screen and (max-width: 769px) {
        margin-top: 20px;
      }
    }
  }

  .TogglePassword {
    font-size: 2.5rem;
    color: ${palette.lightgray};
    position: absolute;
    left: calc(100% - 4.5rem);
    bottom: 45px;
    cursor: pointer;
  }

  .TogglePasswordContainer {
    width: 100%;
    padding: 0;
    margin: 0;
    position: relative;
  }

  @media screen and (max-width: 769px) {
    padding: 0px;
    flex-wrap: wrap;
  }
  .submit-btn {
    color: #fff;
    background-color: #0f3f44;
    display: block;
    margin-top: 16px;
    width: 100%;
    padding: 16px 32px;
    border-radius: 32px;
    margin-top: 32px;
    border: none;
    &:hover {
      cursor: pointer;
    }
  }
  .cancel {
    color: ${palette.red};
    background: transparent;
    width: 100%;
    padding: 16px 32px;
    border-radius: 32px;
    display: block;
  }
  .k-dialog-titlebar {
    background-color: #0f3f44;
  }
`;
